import React, { useEffect, useState, useRef, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faTrash,
  faPlus,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast.jsx";
import LoadingSpinner from "../../components/LoadingSpinner.jsx";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";
import Swal from "sweetalert2";

const ManageLeadSourceCost = () => {
  const [leadSourceCosts, setLeadSourceCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const toastShownRef = useRef(false);

  // Search filter states
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [leadSourceFilter, setLeadSourceFilter] = useState("");
  const [costFilter, setCostFilter] = useState("");

  // Pagination states
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const [showingRecords, setShowingRecords] = useState("");
  const limit = 10;

  // Fetch lead source costs from API
  const fetchLeadSourceCosts = async (pageNum = 0) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_URL}lead-source-cost`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
        params: {
          page: pageNum,
          limit: limit,
        },
      });

      if (response.data) {
        setLeadSourceCosts(response.data.leadsourcecost || []);
        setTotal(response.data.total || 0);
        setShowingRecords(response.data.showingRecords || "");

        // Calculate page count based on total records
        const calculatedPageCount = Math.ceil((response.data.total || 0) / limit);
        setPageCount(calculatedPageCount);

        if (!toastShownRef.current) {
          showToast.success("Lead source costs fetched successfully.", "success");
          toastShownRef.current = true;
        }
      }
    } catch (error) {
      if (!toastShownRef.current) {
        showToast.error("Failed to fetch lead source costs.", "error");
        toastShownRef.current = true;
      }
      console.error("Error fetching lead source costs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeadSourceCosts(page);
  }, [page]);

  // Filter lead source costs based on search criteria (client-side filtering)
  const filteredLeadSourceCosts = useMemo(() => {
    return leadSourceCosts.filter((item) => {
      // Start Date filter
      if (
        startDateFilter &&
        !item.start_date.toLowerCase().includes(startDateFilter.toLowerCase())
      ) {
        return false;
      }

      // End Date filter
      if (
        endDateFilter &&
        !item.end_date.toLowerCase().includes(endDateFilter.toLowerCase())
      ) {
        return false;
      }

      // Lead Source filter
      if (
        leadSourceFilter &&
        !item.lead_source.toLowerCase().includes(leadSourceFilter.toLowerCase())
      ) {
        return false;
      }

      // Cost filter
      if (
        costFilter &&
        !item.cost.toString().toLowerCase().includes(costFilter.toLowerCase())
      ) {
        return false;
      }

      return true;
    });
  }, [leadSourceCosts, startDateFilter, endDateFilter, leadSourceFilter, costFilter]);

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < pageCount) {
      setPage(newPage);
      // Fetch new page data from server
      fetchLeadSourceCosts(newPage);
    }
  };

  // Calculate display indices for current page
  const start = (page * limit) + 1;
  const end = Math.min((page + 1) * limit, total);
  const displayItems = filteredLeadSourceCosts;

  const handleAdd = () => {
    navigate("/Website/LeadSourceCost/add");
  };

  const handleEdit = (id) => {
    navigate(`/Website/LeadSourceCost/edit/${id}`);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem("authToken");
        await axios.delete(`${API_URL}lead-source-cost/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        showToast.success("Record deleted successfully.", "success");
        // Refresh the current page data
        fetchLeadSourceCosts(page);
      } catch (error) {
        showToast.error("Failed to delete record.", "error");
        console.error("Error deleting record:", error);
      }
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      Swal.fire({
        title: "Cancelled",
        text: "Your record is safe :)",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    }
  };

  const handleDetails = async (id) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_URL}lead-source-cost/details/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      Swal.fire({
        title: "Lead Source Cost Details",
        html: `
          <div class="text-left">
            <p><strong>ID:</strong> ${response.data.id}</p>
            <p><strong>Start Date:</strong> ${response.data.start_date}</p>
            <p><strong>End Date:</strong> ${response.data.end_date}</p>
            <p><strong>Lead Source:</strong> ${response.data.lead_source}</p>
            <p><strong>Cost:</strong> ${response.data.cost}</p>
            <p><strong>Per Day Cost:</strong> ${response.data.per_day_cost}</p>
            <p><strong>Total Days:</strong> ${response.data.total_days}</p>
            <p><strong>Created On:</strong> ${response.data.created_on}</p>
            <p><strong>Updated On:</strong> ${response.data.updated_on}</p>
          </div>
        `,
        width: 600,
        showCloseButton: true,
        showConfirmButton: false,
      });
    } catch (error) {
      showToast.error("Failed to fetch details.", "error");
      console.error("Error fetching details:", error);
    }
  };


  return (
    <div className="page-container page-header-fixed page-sidebar-fixed fade in mt-5">
      {/* {loading} */}
      <div className="">
        <div className="panel panel-default with-rounded-corner with-shadow p-10">
          <div className="panel-heading border-0">
            <div className="row">
              <div className="col-md-6">
                <h4 className="panel-title pull-left">Manage Lead Source Cost</h4>
              </div>
              <div className="col-md-6" style={{ textAlign: "right" }}>
                <PermissionWrapper module="website_leadsourcecost" action="add">
                  <button
                    className="btn btn-default btn-sm pull-right me-2 ms-2"
                    onClick={handleAdd}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Add Lead Source Cost
                  </button>
                </PermissionWrapper>
                {/* Pagination Controls */}
                {leadSourceCosts.length > 0 && (
                  <div className="d-inline-block">
                    <span style={{ fontSize: "0.8rem" }} className="me-2">
                      {showingRecords || `${start} to ${end} of ${total}`}
                    </span>
                    <button
                      className="btn btn-default btn-sm"
                      title="Previous"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 0}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    &nbsp;
                    <button
                      className="btn btn-default btn-sm"
                      title="Next"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === pageCount - 1}
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {loading || leadSourceCosts.length > 0 ? (
            <div className="table-responsive user-table-container">
              <style>
                {`
                  .user-table-container {
                    position: relative;
                    max-height: 75vh;
                    overflow-y: auto;
                    overflow-x: auto;
                   }

                  /* First header row (titles) */
                  .user-table-container table thead tr:nth-child(1) th {
                    position: sticky;
                    top: 0;
                    z-index: 30;
                    background: #f8f9fa;
                  }

                 /* Second header row (search inputs) */
                 .user-table-container table thead tr:nth-child(2) th {
                   position: sticky;
                   top: 25px; /* adjust based on header height */
                   z-index: 25;
                   background: #fff;
                  }
               `}
              </style>
              <table className="table table-bordered bg-white m-b-0">
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>#</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Lead Source</th>
                    <th>Cost</th>
                    <th>Per Day Cost</th>
                    <th style={{ width: "1%" }} colSpan="2">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    Array.from({ length: 10 }).map((_, rowIndex) => (
                      <tr key={rowIndex} className="skeleton-row">
                        {Array.from({ length: 8 }).map((_, colIndex) => (
                          <td key={colIndex}>
                            <div className="skeleton-box"></div>
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : filteredLeadSourceCosts.length > 0 ? (
                    filteredLeadSourceCosts.map((item, index) => (
                      <tr key={item.id}>
                        <td>{start + index}</td>
                        <td>{item.start_date}</td>
                        <td>{item.end_date}</td>
                        <td>{item.lead_source}</td>
                        <td>{item.cost.toLocaleString()}</td>
                        <td>{item.per_day_cost?.toFixed(2)}</td>
                        <td>
                          <PermissionWrapper module="website_leadsourcecost" action="edit">
                            <button
                              className="btn btn-warning btn-xs"
                              onClick={() => navigate(`/Website/LeadSourceCost/edit/${item.id}`)}
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </button>
                          </PermissionWrapper>
                        </td>
                        <td>
                          <PermissionWrapper module="website_leadsourcecost" action="delete">
                            <button
                              className="btn btn-danger btn-xs"
                              onClick={() => handleDelete(item.id)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </PermissionWrapper>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center">
                        No records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-3 text-center">
              <p>No records found.</p>
              <PermissionWrapper module="website_leadsourcecost" action="add">
                <button
                  className="btn btn-default btn-sm"
                  onClick={handleAdd}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Add Lead Source Cost
                </button>
              </PermissionWrapper>
            </div>
          )}
        </div>
      </div>

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default ManageLeadSourceCost;